import { Injectable, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { OneSignal as OneSignalWeb } from 'onesignal-ngx';
import OneSignalCap, { LogLevel } from '@onesignal/capacitor-plugin';
import { PushNotificationService } from './push-notification.service';
import { AuthService } from './auth.service';
import { DeepLinkService } from './deep-link.service';
import { NotificationPermissionService } from './notification-permission.service';
import { SensoryFeedbackService } from './haptic.service';
import * as Sentry from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class PushNotificationInitService {
  private oneSignalWeb = inject(OneSignalWeb);
  private pushService = inject(PushNotificationService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private deepLinkService = inject(DeepLinkService);
  private notifPermission = inject(NotificationPermissionService);
  private sensoryFeedback = inject(SensoryFeedbackService);

  /** Whether the current device has granted push notification permission */
  pushPermissionGranted = signal(false);

  private readonly ONESIGNAL_APP_ID = 'a4ab062d-ef79-4424-bc36-317713281483';

  async initialize(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.initializeNativeOneSignal();
    } else {
      await this.initializeWebOneSignal();
    }
    this.setupClickHandler();
  }

  /**
   * Called after the user grants notification permission (e.g., from the permission modal).
   */
  async handleNativePermissionGranted(): Promise<void> {
    try {
      const subId = await OneSignalCap.User.pushSubscription.getIdAsync();
      if (subId) {
        this.pushService.setPlayerId(subId);
        await this.syncDeviceWithBackend();
        this.pushPermissionGranted.set(true);
      }
    } catch (error) {
      console.error('Failed to complete OneSignal native setup after permission grant:', error);
      Sentry.captureException(error);
    }
  }

  /**
   * Requests push permission (if not already granted), updates the internal state,
   * and returns whether permission was granted.
   *
   * Call this before trying to create a monitor with push notifications.
   */
  async ensurePushPermission(): Promise<boolean> {
    // Already granted?
    if (this.pushPermissionGranted()) return true;

    // Use the existing permission‑request service (handles both platforms)
    try {
      const granted = await this.notifPermission.requestPermission();
      if (!granted) return false;
    } catch (err) {
      console.error('Permission request failed', err);
      Sentry.captureException(err);
      return false;
    }

    // After granting, re‑fetch the subscription ID so the push service is current.
    if (Capacitor.isNativePlatform()) {
      try {
        const subId = await OneSignalCap.User.pushSubscription.getIdAsync();
        if (subId) {
          this.pushService.setPlayerId(subId);
          await this.syncDeviceWithBackend();
          this.pushPermissionGranted.set(true);
          return true;
        }
      } catch (e) {
        console.error('Failed to get subscription id after permission', e);
        Sentry.captureException(e);
      }
    } else {
      const osGlobal = (window as any).OneSignal;
      const subId = osGlobal?.User?.PushSubscription?.id;
      if (subId) {
        this.pushService.setPlayerId(subId);
        await this.syncDeviceWithBackend();
        this.pushPermissionGranted.set(true);
        return true;
      } else {
        // The subscription may take a moment after the permission prompt.
        await new Promise(r => setTimeout(r, 1000));
        const retrySubId = osGlobal?.User?.PushSubscription?.id;
        if (retrySubId) {
          this.pushService.setPlayerId(retrySubId);
          await this.syncDeviceWithBackend();
          this.pushPermissionGranted.set(true);
          return true;
        }
      }
    }

    return false;
  }

  private async initializeNativeOneSignal(): Promise<void> {
    try {
      OneSignalCap.Debug.setLogLevel(LogLevel.Verbose);
      await OneSignalCap.initialize(this.ONESIGNAL_APP_ID);

      this.setupNativeForegroundListener();

      const subId = await OneSignalCap.User.pushSubscription.getIdAsync();
      if (subId) {
        this.pushService.setPlayerId(subId);
        await this.syncDeviceWithBackend();
        this.pushPermissionGranted.set(true);
      }
    } catch (error) {
      console.error('OneSignal native initialization failed:', error);
      Sentry.captureException(error);
    }
  }

  private async initializeWebOneSignal(): Promise<void> {
    try {
      await this.oneSignalWeb.init({
        appId: this.ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
      });

      this.setupWebForegroundListener();

      const osGlobal = (window as any).OneSignal;
      const subId = osGlobal?.User?.PushSubscription?.id;

      if (subId) {
        this.pushService.setPlayerId(subId);
        await this.syncDeviceWithBackend();
        this.pushPermissionGranted.set(true);
      }
    } catch (error) {
      console.error('OneSignal web initialization failed:', error);
      Sentry.captureException(error);
    }
  }

  private async syncDeviceWithBackend() {
    if (this.pushService.ready()) {
      const platform = Capacitor.isNativePlatform()
        ? Capacitor.getPlatform() === 'ios'
          ? 'ios'
          : 'android'
        : 'web';
      await this.pushService.registerDevice(platform);
    }
  }

  // ---------- Foreground notification handling ----------

  private setupNativeForegroundListener(): void {
    try {
      OneSignalCap.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
        this.handleForegroundNotification(event.notification);
      });
    } catch (e) {
      console.error('Failed to add native foreground listener', e);
      Sentry.captureException(e);
    }
  }

  private setupWebForegroundListener(): void {
    try {
      const os = (window as any).OneSignal;
      if (os?.Notifications) {
        os.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
          this.handleForegroundNotification(event.notification);
        });
      } else {
        // Retry once after a short delay; OneSignal SDK may still be initialising
        setTimeout(() => {
          const retryOs = (window as any).OneSignal;
          if (retryOs?.Notifications) {
            retryOs.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
              this.handleForegroundNotification(event.notification);
            });
          }
        }, 1000);
      }
    } catch (e) {
      console.error('Failed to add web foreground listener', e);
      Sentry.captureException(e);
    }
  }

  private handleForegroundNotification(notificationData: any): void {
    if (!notificationData) return;

    // Extract additional data – different SDK versions may place it in different locations
    const additionalData =
      notificationData?.additionalData ||
      notificationData?.rawPayload?.additionalData ||
      notificationData?.data;

    if (!additionalData) return;

    const isIntentHit =
      additionalData.type === 'intent_hit' ||
      additionalData.category === 'intent_hit' ||
      additionalData.intentHit === true ||
      additionalData.intent_hit === true;

    const isHighPriority =
      additionalData.priority === 'high' ||
      additionalData.importance === 'high' ||
      additionalData.highPriority === true;

    if (isIntentHit || isHighPriority) {
      try {
        this.sensoryFeedback.triggerAlert();
      } catch (error) {
        // Never block the app due to sensory feedback, but forward to Sentry
        Sentry.captureException(error);
      }
    }
  }

  // ---------- Click (deep link) handling ----------

  private setupClickHandler() {
    if (Capacitor.isNativePlatform()) {
      OneSignalCap.Notifications.addEventListener('click', (event: any) => {
        this.handleNotificationClick(event.notification);
      });
    } else {
      const os = (window as any).OneSignal;
      if (os?.Notifications) {
        os.Notifications.addEventListener('click', (event: any) => {
          this.handleNotificationClick(event.notification);
        });
      } else {
        // Retry once after a short delay; OneSignal SDK may still be initialising
        setTimeout(() => {
          const retryOs = (window as any).OneSignal;
          if (retryOs?.Notifications) {
            retryOs.Notifications.addEventListener('click', (event: any) => {
              this.handleNotificationClick(event.notification);
            });
          }
        }, 1000);
      }
    }
  }

  private handleNotificationClick(notification: any) {
    const deeplink = notification?.additionalData?.deeplink;
    if (!deeplink || typeof deeplink !== 'string') {
      return;
    }

    // Normalise the deeplink to an internal router path (e.g. /monitors/123/events?ids=456)
    const internalPath = this.deepLinkService.normalizeUrl(deeplink);

    if (this.auth.isLoggedIn()) {
      // User is already authenticated → go straight to the deep‑linked page
      this.router.navigateByUrl(internalPath, { replaceUrl: true });
    } else {
      // Not authenticated – store the link so it can be used after login
      this.deepLinkService.setDeepLink(internalPath);
      this.router.navigate(['/auth'], { replaceUrl: true });
    }
  }
}
